export const MEETING_MODES = [
  ["in_person", "In person"],
  ["online", "Online"],
  ["hybrid", "Hybrid"],
];

export const ONLINE_PROVIDERS = [
  ["zoom", "Zoom"],
  ["teams", "Microsoft Teams"],
  ["webex", "Cisco Webex"],
  ["youtube", "YouTube"],
  ["facebook", "Facebook"],
  ["custom", "Custom link"],
];

export const ONLINE_START_VISIBILITY = [
  ["always", "Always"],
  ["day_of", "Day of service/event"],
  ["30_min_before", "30 minutes before"],
  ["during_service", "During service/event only"],
];

function cleanText(value, fallback = "") {
  const text = String(value || "").trim();
  return text || fallback;
}

export function normalizeMeetingMode(value) {
  const text = cleanText(value, "in_person").toLowerCase().replace(/-/g, "_");
  if (text === "online" || text === "hybrid" || text === "in_person") return text;
  return "in_person";
}

export function normalizeOnlineProvider(value) {
  const text = cleanText(value, "custom").toLowerCase().replace(/microsoft[-_\s]?teams/g, "teams");
  if (text.includes("zoom")) return "zoom";
  if (text.includes("team")) return "teams";
  if (text.includes("webex") || text.includes("cisco")) return "webex";
  if (text.includes("youtube") || text.includes("youtu.be")) return "youtube";
  if (text.includes("facebook") || text.includes("fb.")) return "facebook";
  return ONLINE_PROVIDERS.some(([key]) => key === text) ? text : "custom";
}

export function sanitizeOnlineUrl(value) {
  const text = cleanText(value);
  if (!text) return "";
  try {
    const url = new URL(text);
    return url.protocol === "https:" ? url.toString() : "";
  } catch {
    return "";
  }
}

export function looksLikePrivateHostUrl(value) {
  const text = cleanText(value).toLowerCase();
  return Boolean(
    text &&
      (text.includes("start_url=") ||
        text.includes("/start/") ||
        text.includes("/host/") ||
        text.includes("zak=") ||
        text.includes("host_key="))
  );
}

export function getOnlineProviderLabel(provider) {
  return ONLINE_PROVIDERS.find(([key]) => key === normalizeOnlineProvider(provider))?.[1] || "Online";
}

export function getDefaultOnlineButtonLabel(provider) {
  const normalized = normalizeOnlineProvider(provider);
  if (normalized === "zoom") return "Join Zoom";
  if (normalized === "teams") return "Join Teams";
  if (normalized === "webex") return "Join Webex";
  if (normalized === "youtube" || normalized === "facebook") return "Watch Live";
  return "Join Online Service";
}

export function normalizeVisibility(value) {
  const text = cleanText(value, "always").toLowerCase();
  return ONLINE_START_VISIBILITY.some(([key]) => key === text) ? text : "always";
}

function parseDateLike(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value?.toDate === "function") {
    const date = value.toDate();
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function timeParts(value, fallbackHour = 10, fallbackMinute = 0) {
  const [hour, minute] = String(value || "").split(":");
  const h = Number(hour);
  const m = Number(minute);
  return {
    hour: Number.isFinite(h) ? h : fallbackHour,
    minute: Number.isFinite(m) ? m : fallbackMinute,
  };
}

export function getEventOnlineWindow(item = {}) {
  const dateText = item.startDate || item.date || item.serviceDate || "";
  const startDate = parseDateLike(item.startsAt || item.startAt || item.startDateTime);
  let start = startDate;

  if (!start && dateText) {
    const base = parseDateLike(`${dateText}T00:00:00`);
    if (base) {
      const { hour, minute } = timeParts(item.startTime || item.time);
      base.setHours(hour, minute, 0, 0);
      start = base;
    }
  }

  const explicitEnd = parseDateLike(item.endsAt || item.endAt || item.endDateTime);
  let end = explicitEnd;
  if (!end && start) {
    const { hour, minute } = timeParts(item.endTime, start.getHours() + 2, start.getMinutes());
    end = new Date(start);
    end.setHours(hour, minute, 0, 0);
    if (end.getTime() <= start.getTime()) {
      end = new Date(start.getTime() + 2 * 60 * 60 * 1000);
    }
  }

  return { start, end };
}

export function isOnlineJoinVisible({ visibility = "always", start = null, end = null, now = new Date() } = {}) {
  const mode = normalizeVisibility(visibility);
  if (mode === "always") return true;
  if (!start) return false;

  const activeEnd = end && end.getTime() > start.getTime()
    ? end
    : new Date(start.getTime() + 2 * 60 * 60 * 1000);

  if (mode === "day_of") {
    return (
      now.getFullYear() === start.getFullYear() &&
      now.getMonth() === start.getMonth() &&
      now.getDate() === start.getDate()
    );
  }

  if (mode === "30_min_before") {
    const opensAt = new Date(start.getTime() - 30 * 60 * 1000);
    return now.getTime() >= opensAt.getTime() && now.getTime() <= activeEnd.getTime();
  }

  if (mode === "during_service") {
    return now.getTime() >= start.getTime() && now.getTime() <= activeEnd.getTime();
  }

  return false;
}

export function normalizeOnlineMeeting(item = {}, options = {}) {
  const online = item.online || {};
  const meetingMode = normalizeMeetingMode(
    item.meetingMode || item.occurrenceType || online.meetingMode || online.mode
  );
  const onlineProvider = normalizeOnlineProvider(
    item.onlineProvider || online.provider || online.onlineProvider
  );
  const onlineUrl = sanitizeOnlineUrl(
    item.onlineUrl || online.joinUrl || online.url || online.onlineUrl
  );
  const onlineButtonLabel = cleanText(
    item.onlineButtonLabel || online.buttonLabel || online.onlineButtonLabel,
    getDefaultOnlineButtonLabel(onlineProvider)
  );
  const onlineStartVisibility = normalizeVisibility(
    item.onlineStartVisibility || online.startVisibility || online.onlineStartVisibility
  );
  const onlineInstructions = cleanText(
    item.onlineInstructions || online.instructions || online.onlineInstructions
  );
  const windowInfo = options.window || getEventOnlineWindow(item);
  const canShow =
    (meetingMode === "online" || meetingMode === "hybrid") &&
    Boolean(onlineUrl) &&
    isOnlineJoinVisible({
      visibility: onlineStartVisibility,
      start: windowInfo.start,
      end: windowInfo.end,
      now: options.now || new Date(),
    });

  return {
    meetingMode,
    onlineProvider,
    onlineUrl,
    onlineButtonLabel,
    onlineStartVisibility,
    onlineInstructions,
    providerLabel: getOnlineProviderLabel(onlineProvider),
    canShow,
    hasOnlineUrl: Boolean(onlineUrl),
    isPrivateHostUrl: looksLikePrivateHostUrl(item.onlineUrl || online.joinUrl || online.url || online.onlineUrl),
  };
}
